import React, { memo, useState } from 'react'
import cn, { Argument as ClassName } from 'classnames'
import { useNavigate, useParams } from 'react-router-dom'

import { PageLayoutContainer } from '../../../lib/page-layout-container'
import { PoolDetailsWidget } from '../../components/PoolDetailsWidget'
import { WidgetLayout } from '../../../lib/widget-layout'
import {
    usePoolGetByIdQuery,
    ChargePoint,
    usePoolGetChargePointsQuery,
    useChargePointAddToPoolMutation,
} from '../../../common/redux/api/web-api'
import { ChargersListWidget } from '../../../lib/chargers-list-widget'
import Text from '../../../ui/Text'
import { notifyError, notifySuccess } from '../../../common/features/notificationsManager/Notify'
import { AddChargePointToPoolModal } from '../../components/AddChargePointToPoolModal'
import useText from '../../../common/hooks/useText'

export type PoolPageProps = {
    className?: ClassName
    style?: React.CSSProperties
}

export const PoolPage = memo(function PoolPageProps(props: PoolPageProps): React.ReactElement {
    const text = useText()
    const navigate = useNavigate()
    const [addChargersModalIsOpened, setAddChargersModalIsOpened] = useState(false)

    const { poolId: currentPoolId } = useParams()
    const { data: poolChargePoints } = usePoolGetChargePointsQuery(
        {
            poolId: currentPoolId,
        },
        { refetchOnMountOrArgChange: true }
    )

    const { currentData: poolById } = usePoolGetByIdQuery({
        poolId: currentPoolId,
    })

    const [addChargePointToPool] = useChargePointAddToPoolMutation()

    const saveEdit = () => {
        notifySuccess(text('msgPoolUpdated'))
    }

    const handleAddChargerToPool = (chargePoint: ChargePoint) => {
        addChargePointToPool({ chargePointId: chargePoint.id, poolId: currentPoolId })
            .unwrap()
            .then(() => {
                notifySuccess(text('lblChargerWasAddedToPool'))
            })
            .catch(() => notifyError('msgChargerFailedToAddToPool'))
    }

    return (
        <>
            <PageLayoutContainer className={cn(props.className)} style={props.style}>
                <div className="row margin-bottom-small margin-right-small">
                    <div className="pull-right">
                        <button
                            type="button"
                            data-testid="back-btn"
                            className="btn btn-ok btn-sm"
                            onClick={() => navigate('/pools')}
                        >
                            <Text code="btnBack" />
                        </button>
                    </div>
                </div>
                <WidgetLayout title={text('lblPoolPage', { name: poolById?.name })}>
                    <PoolDetailsWidget save={saveEdit} poolName={poolById?.name} poolValue={poolById} />
                </WidgetLayout>

                <ChargersListWidget
                    poolChargePoints={poolChargePoints}
                    onAddChargerToPool={() => setAddChargersModalIsOpened(true)}
                />
                {addChargersModalIsOpened && (
                    <AddChargePointToPoolModal
                        onAdd={(chargePoint) => handleAddChargerToPool(chargePoint)}
                        onCancel={() => setAddChargersModalIsOpened(false)}
                    />
                )}
            </PageLayoutContainer>
        </>
    )
})
